/**
 * Email Verification Page
 * Enter verification code sent via email
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { userPool } from '@/utils/auth';

export const VerifyEmailPage = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!email) {
      showNotification('error', 'Email address is required');
      navigate('/register');
    }
  }, [email, navigate, showNotification]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      await new Promise<void>((resolve, reject) => {
        cognitoUser.confirmRegistration(verificationCode, true, (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      });

      showNotification('success', 'Email verified successfully! You can now log in.');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (err: any) {
      console.error('Verification error:', err);
      
      if (err.code === 'CodeMismatchException') {
        setError('Invalid verification code. Please check and try again.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else if (err.code === 'NotAuthorizedException') {
        setError('User is already verified. Please log in.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      await new Promise<void>((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      });

      showNotification('success', 'Verification code sent! Check your email.');
    } catch (err: any) {
      console.error('Resend error:', err);
      showNotification('error', err.message || 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/admin/icon.png" alt="SpellTales" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
            Verify Your Email
          </h1>
          <p className="text-slate-600">
            We sent a verification code to<br />
            <strong>{email}</strong>
          </p>
        </div>

        {/* Verification Form */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800">
              <strong>Check your email</strong> for a 6-digit verification code and enter it below.
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-6">
            <Input
              label="Verification Code"
              type="text"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                setError('');
              }}
              error={error}
              placeholder="123456"
              required
              autoComplete="off"
              maxLength={6}
              helperText="Enter the 6-digit code from your email"
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              className="w-full"
            >
              Verify Email
            </Button>
          </form>

          {/* Resend Code */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-600 mb-3">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResendCode}
              disabled={isResending}
              className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm disabled:opacity-50"
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </button>
          </div>

          {/* Back to Login */}
          <div className="mt-4 pt-4 border-t border-slate-200 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Back to Login
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p>© 2026 SpellTales. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
